import { useState, useEffect, useCallback } from 'react';

export type ConsentStatus = 'pending' | 'accepted' | 'refused';

interface CookieConsent {
  analytics: boolean;
  timestamp: number;
}

const CONSENT_KEY = 'piktable_cookie_consent';
const META_PIXEL_ID = '1512859300018871';

function loadMetaPixel() {
  if (typeof window.fbq !== 'function') return;
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

function grantAnalytics() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': 'granted'
    });
  }
  loadMetaPixel();
}

function denyAnalytics() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied'
    });
  }
}

export function hasAnalyticsConsent(): boolean {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return false;
    const consent: CookieConsent = JSON.parse(stored);
    return consent.analytics === true;
  } catch {
    return false;
  }
}

export const useCookieConsent = () => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [isVisible, setIsVisible] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        const consent: CookieConsent = JSON.parse(stored);
        if (consent.analytics) {
          setConsentStatus('accepted');
          grantAnalytics();
        } else {
          setConsentStatus('refused');
          denyAnalytics();
        }
        setIsVisible(false);
      } catch {
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const consent: CookieConsent = {
      analytics: true,
      timestamp: Date.now()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentStatus('accepted');
    setIsVisible(false);
    grantAnalytics();
  }, []);

  const refuseAll = useCallback(() => {
    const consent: CookieConsent = {
      analytics: false,
      timestamp: Date.now()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setConsentStatus('refused');
    setIsVisible(false);
    denyAnalytics();
  }, []);

  const openPreferences = useCallback(() => {
    setIsVisible(true);
  }, []);

  return {
    consentStatus,
    isVisible,
    acceptAll,
    refuseAll,
    openPreferences
  };
};

// Extend Window interface for gtag and fbq
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: (...args: unknown[]) => void;
  }
}
